import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLabelHistory, LabelPrint } from '@/hooks/useLabelHistory';
import { useActivityLogger } from '@/hooks/useActivityLogger';
import { printLabels } from '@/lib/browserPrint';
import { LABEL_SHEET_PRESETS, FieldData, createSingleLabelConfig } from '@/types/label';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LabelThumbnail } from '@/components/LabelThumbnail';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ArrowLeft,
  Search,
  Printer,
  Copy,
  Calendar,
  FileText,
  Download,
  Loader2,
  History,
  Eye,
  RefreshCw,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const FieldDataPreview: React.FC<{ fieldData: FieldData }> = ({ fieldData }) => {
  const entries = Object.entries(fieldData).slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
      {entries.map(([key, value]) => (
        <Badge key={key} variant="outline" className="text-[10px] font-normal py-0 px-1.5 max-w-[200px] truncate">
          {key}: {String(value)}
        </Badge>
      ))}
      {Object.keys(fieldData).length > 3 && (
        <span className="text-[10px] text-muted-foreground">+{Object.keys(fieldData).length - 3} more</span>
      )}
    </div>
  );
};

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin, hasPermission } = useAuth();
  const { prints, loading, totalCount, fetchHistory, saveLabelPrint, exportHistory } = useLabelHistory();
  const { logActivity } = useActivityLogger();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [copiesFilter, setCopiesFilter] = useState<string>('all');
  const [allUsers, setAllUsers] = useState<{ user_id: string; username: string }[]>([]);
  const [selectedPrint, setSelectedPrint] = useState<LabelPrint | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reprinting, setReprinting] = useState(false);

  const canPrint = hasPermission('print_labels');
  const canReprint = canPrint && hasPermission('reprint_labels' as any);
  const isHistoryViewerOnly = hasPermission('view_label_history' as any) && !canPrint && !isAdmin;

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    if (isAdmin) {
      supabase.from('profiles').select('user_id, username').order('username').then(({ data }) => {
        setAllUsers((data || []) as { user_id: string; username: string }[]);
      });
    }
  }, [isAdmin]);

  const buildFilters = useCallback(() => ({
    search: debouncedSearch || undefined,
    templateName: templateFilter || undefined,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : undefined,
    userId: userFilter || undefined,
    printSource: sourceFilter,
    copiesRange: copiesFilter,
  }), [debouncedSearch, templateFilter, dateFrom, dateTo, userFilter, sourceFilter, copiesFilter]);

  useEffect(() => {
    fetchHistory(buildFilters());
  }, [debouncedSearch, templateFilter, dateFrom, dateTo, userFilter, sourceFilter, copiesFilter, fetchHistory, buildFilters]);

  const hasActiveFilters = debouncedSearch || dateFrom || dateTo || userFilter || sourceFilter !== 'all' || copiesFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    setUserFilter('');
    setSourceFilter('all');
    setCopiesFilter('all');
  };

  const handleReprint = useCallback(async (print: LabelPrint) => {
    if (!canReprint || reprinting) return;
    setReprinting(true);
    try {
      const template = print.template_snapshot;
      const fieldData = print.field_data;
      const sheetConfig = print.sheet_config || createSingleLabelConfig(template.width, template.height);

      const resolveContent = (content: string) => {
        return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
          return fieldData[fieldName] || match;
        });
      };

      const data: FieldData[] = Array(print.copies).fill(fieldData);
      await printLabels(template, resolveContent, sheetConfig, data);

      const newPrintId = await saveLabelPrint({
        template,
        fieldData,
        copies: print.copies,
        printSource: 'reprint',
        sheetConfig: sheetConfig,
        originalPrintId: print.id,
      });

      logActivity('reprint', 'print', `Reprinted label ${print.id}`, {
        originalPrintId: print.id,
        newPrintId,
        templateName: template.name,
      });

      toast.success('Reprint dialog opened');
      fetchHistory(buildFilters());
    } catch (error) {
      console.error('Reprint failed:', error);
      toast.error('Failed to reprint label');
    } finally {
      setReprinting(false);
    }
  }, [canReprint, reprinting, saveLabelPrint, logActivity, fetchHistory, buildFilters]);

  const handleViewDetails = (print: LabelPrint) => {
    setSelectedPrint(print);
    setDetailOpen(true);
  };

  const sourceLabel = (source: string) => {
    switch (source) {
      case 'editor': return 'Editor';
      case 'kiosk': return 'Kiosk';
      case 'reprint': return 'Reprint';
      default: return source;
    }
  };

  const sourceBadgeVariant = (source: string): 'default' | 'secondary' | 'outline' => {
    switch (source) {
      case 'reprint': return 'secondary';
      case 'kiosk': return 'outline';
      default: return 'default';
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-screen bg-workspace">
        {/* Header */}
        <header className="flex items-center justify-between h-14 px-4 border-b border-border/50 bg-card shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <History className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-semibold">Label History</h1>
            <Badge variant="secondary">{totalCount} labels</Badge>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => exportHistory({ userId: userFilter || undefined })}>
                <Download className="w-4 h-4 mr-1.5" />
                Export CSV
              </Button>
            )}
          </div>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/50">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search template, field data, or label ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="editor">Editor</SelectItem>
              <SelectItem value="kiosk">Kiosk</SelectItem>
              <SelectItem value="reprint">Reprint</SelectItem>
            </SelectContent>
          </Select>
          <Select value={copiesFilter} onValueChange={setCopiesFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Copies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All copies</SelectItem>
              <SelectItem value="1">1 copy</SelectItem>
              <SelectItem value="2-5">2–5 copies</SelectItem>
              <SelectItem value="6-10">6–10 copies</SelectItem>
              <SelectItem value="10+">10+ copies</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-40"
            placeholder="From date"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-40"
            placeholder="To date"
          />
          {isAdmin && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {allUsers.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchHistory(buildFilters())}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5 text-muted-foreground">
              <X className="w-3.5 h-3.5" />
              Clear
            </Button>
          )}
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3 max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : prints.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No labels found</p>
                <p className="text-sm">Printed labels will appear here automatically</p>
              </div>
            ) : (
              prints.map((print) => (
                <Card key={print.id} className="hover:border-primary/30 transition-colors">
                  <CardContent className="flex items-center gap-4 py-4">
                    <LabelThumbnail
                      template={print.template_snapshot}
                      fieldData={print.field_data}
                      scale={0.55}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{print.template_name}</span>
                        <Badge variant={sourceBadgeVariant(print.print_source)} className="text-xs">
                          {sourceLabel(print.print_source)}
                        </Badge>
                        {print.original_print_id && (
                          <Badge variant="outline" className="text-xs">
                            Reprint of {print.original_print_id.slice(0, 8)}…
                          </Badge>
                        )}
                      </div>
                      <FieldDataPreview fieldData={print.field_data} />
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(new Date(print.created_at), 'MMM d, yyyy HH:mm')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Copy className="w-3.5 h-3.5" />
                          {print.copies} {print.copies === 1 ? 'copy' : 'copies'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground/70 font-mono mt-0.5">
                        {print.id}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => handleViewDetails(print)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View details</TooltipContent>
                      </Tooltip>
                      {canReprint && !isHistoryViewerOnly && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReprint(print)}
                              disabled={reprinting}
                              className="gap-1.5"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              Reprint
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reprint with exact original data</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Label Print Details</DialogTitle>
              <DialogDescription>
                Complete snapshot of this print event
              </DialogDescription>
            </DialogHeader>
            {selectedPrint && (
              <div className="space-y-4">
                <div className="flex justify-center p-3 bg-muted/30 rounded-lg">
                  <LabelThumbnail
                    template={selectedPrint.template_snapshot}
                    fieldData={selectedPrint.field_data}
                    scale={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Label ID</span>
                    <p className="font-mono text-xs break-all">{selectedPrint.id}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Template</span>
                    <p className="font-medium">{selectedPrint.template_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Source</span>
                    <p>{sourceLabel(selectedPrint.print_source)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Copies</span>
                    <p>{selectedPrint.copies}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Printed At</span>
                    <p>{format(new Date(selectedPrint.created_at), 'MMM d, yyyy HH:mm:ss')}</p>
                  </div>
                  {selectedPrint.original_print_id && (
                    <div>
                      <span className="text-muted-foreground">Original Print</span>
                      <p className="font-mono text-xs break-all">{selectedPrint.original_print_id}</p>
                    </div>
                  )}
                </div>

                <div>
                  <span className="text-sm text-muted-foreground">Field Data</span>
                  <div className="mt-1 p-3 rounded-md bg-muted/50 text-sm space-y-1">
                    {Object.entries(selectedPrint.field_data).length === 0 ? (
                      <p className="text-muted-foreground">No field data</p>
                    ) : (
                      Object.entries(selectedPrint.field_data).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <span className="font-medium min-w-[80px]">{key}:</span>
                          <span className="break-all">{String(value)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              {canReprint && !isHistoryViewerOnly && selectedPrint && (
                <Button onClick={() => { setDetailOpen(false); handleReprint(selectedPrint); }} disabled={reprinting}>
                  <Printer className="w-4 h-4 mr-1.5" />
                  Reprint This Label
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default HistoryPage;